import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { startArticleTransition } from './article-transition.js';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// ---- Word split ----

const splitWords = (el) => {
    const temp = document.createElement('div');
    temp.innerHTML = el.innerHTML;
    const walker = document.createTreeWalker(temp, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
        if (node.nodeValue.trim()) textNodes.push(node);
    }
    textNodes.forEach((textNode) => {
        const words = textNode.nodeValue.split(/(\s+)/);
        const fragment = document.createDocumentFragment();
        words.forEach((word) => {
            if (word.match(/^\s+$/)) {
                fragment.appendChild(document.createTextNode(word));
            } else {
                const span = document.createElement('span');
                span.classList.add('word');
                span.style.cssText = 'display:inline-block; will-change: opacity, filter, transform;';
                span.textContent = word;
                fragment.appendChild(span);
            }
        });
        textNode.parentNode.replaceChild(fragment, textNode);
    });
    el.innerHTML = temp.innerHTML;
    return Array.from(el.querySelectorAll('.word'));
};

// ---- Bind transition Barba ----

const initProjectLinks = (container) => {
    container.querySelectorAll('.hz-project').forEach((project) => {
        project.addEventListener('click', () => startArticleTransition(project));
    });
};

// ---- Bornes du scroll (progress de #wf-hz-section) ----
// Ordre : présentation (« Je vous propose ») → services → workflow
const INTRO_END = 0.18; // fin du slide intro pinné
const HZ_END    = 0.62; // fin du défilement horizontal des services
const WF_START  = 0.66; // le workflow entre ici (bascule horizontale vers la gauche)
const WF_END    = 0.92; // toutes les étapes sont révélées — le reste sert de pause avant la sortie

const clamp01 = (v) => Math.min(Math.max(v, 0), 1);

// ---- Positions de scroll exportées (ancres / retour d'article) ----
// 'workflow'               : Workflow affiché, toutes les étapes révélées
// 'hz-slide-01' / '…-02'   : slide service correspondant
export const getAboutScrollY = (key) => {
    const section = document.querySelector('#wf-hz-section');
    if (!section) return null;
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const scrollable = section.offsetHeight - window.innerHeight;
    if (key === 'workflow') return sectionTop + Math.min(WF_END + 0.02, 1) * scrollable;
    const slides = section.querySelectorAll('#hz-track .hz-slide').length;
    const index  = key === 'hz-slide-01' ? 1 : key === 'hz-slide-02' ? 2 : null;
    if (index === null || slides < 2) return null;
    return getSnapScrollY(section, index, slides);
};

// ---- Calcul des positions snap ----

const getSnapScrollY = (section, slideIndex, slidesCount) => {
    const sectionTop     = section.getBoundingClientRect().top + window.scrollY;
    const totalHeight    = section.offsetHeight;
    const scrollable     = totalHeight - window.innerHeight;
    const targetHzP      = slideIndex / (slidesCount - 1);
    const targetProgress = INTRO_END + targetHzP * (HZ_END - INTRO_END);
    return sectionTop + targetProgress * scrollable;
};

// ---- Panneau projets ----

const initServicePanels = (container, section, slidesCount, onPanelOpen, onPanelClose) => {
    const slides = container.querySelectorAll('#hz-track .hz-slide');

    slides.forEach((slide, slideIndex) => {
        const openBtn  = slide.querySelector('.hz-open');
        const closeBtn = slide.querySelector('.hz-close');
        const panel    = slide.querySelector('.hz-panel');
        const header   = slide.querySelector('.hz-header');

        if (!openBtn || !panel || !header) return;

        let isOpen    = false;
        let triggered = false;

        const closePanel = (goNext = false) => {
            if (!isOpen) return;
            isOpen    = false;
            triggered = false;

            const content = header.querySelector('.flex.flex-col.justify-center');
            const title   = header.querySelector('.hz-title');

            if (content) gsap.to(content, { y: '0%', duration: 0.65, ease: 'power3.inOut' });
            if (title)   gsap.to(title,   { scale: 1, filter: 'blur(0px)', y: 0, duration: 0.65, ease: 'power3.inOut' });

            gsap.to(panel, {
                y: '100%', duration: 0.65, ease: 'power3.inOut',
                onComplete: () => {
                    panel.scrollTop = 0;
                    triggered = false;
                    const endEl = slide.querySelector('.hz-panel-end');
                    if (endEl) gsap.set(endEl, { opacity: 1, y: 0 });
                    gsap.to(openBtn, { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' });
                    onPanelClose(goNext, slideIndex);
                },
            });
        };

        const openPanel = () => {
            if (isOpen) return;

            const target = getSnapScrollY(section, slideIndex, slidesCount);
            const dist   = Math.abs(window.scrollY - target);

            const doOpen = () => {
                isOpen = true;
                onPanelOpen();

                const content = header.querySelector('.flex.flex-col.justify-center');
                const title   = header.querySelector('.hz-title');

                if (title)   gsap.set(title, { transformOrigin: 'left top' });
                if (content) gsap.to(content, { y: '-28vh', duration: 0.75, ease: 'power3.inOut' });
                if (title)   gsap.to(title,   { scale: 0.4, duration: 0.75, ease: 'power3.inOut' });

                gsap.to(panel,   { y: '0%', duration: 0.75, ease: 'power3.inOut' });
                gsap.to(openBtn, { opacity: 0, y: 4, duration: 0.25, ease: 'power2.in' });
            };

            if (dist > 10) {
                const proxy = { y: window.scrollY };
                gsap.to(proxy, {
                    y: target, duration: 0.6, ease: 'power2.inOut',
                    onUpdate:   () => window.scrollTo(0, Math.round(proxy.y)),
                    onComplete: doOpen,
                });
            } else {
                doOpen();
            }
        };

        const handleWheel = (e) => {
            if (!isOpen) return;
            const atBottom = panel.scrollHeight - panel.scrollTop - panel.clientHeight < 5;
            const atTop    = panel.scrollTop < 5;

            if (atBottom && e.deltaY > 0 && !triggered) {
                e.preventDefault();
                e.stopPropagation();
                triggered = true;
                const endEl = slide.querySelector('.hz-panel-end');
                if (endEl) {
                    gsap.to(endEl, {
                        opacity: 0, y: -10, duration: 0.3, ease: 'power2.in',
                        onComplete: () => closePanel(true),
                    });
                } else {
                    closePanel(true);
                }
                return;
            }
            if (atTop && e.deltaY < 0) {
                e.preventDefault();
                e.stopPropagation();
                closePanel(false);
                return;
            }
            e.stopPropagation();
        };

        panel.addEventListener('wheel', handleWheel, { passive: false });

        let touchStartY = 0;
        panel.addEventListener('touchstart', (e) => { touchStartY = e.touches[0].clientY; }, { passive: true });
        panel.addEventListener('touchmove', (e) => {
            if (!isOpen) return;
            const deltaY   = touchStartY - e.touches[0].clientY;
            const atBottom = panel.scrollHeight - panel.scrollTop - panel.clientHeight < 5;
            const atTop    = panel.scrollTop < 5;
            if (atBottom && deltaY > 0 && !triggered) { e.preventDefault(); triggered = true; closePanel(true); }
            else if (atTop && deltaY < 0) { e.preventDefault(); closePanel(false); }
        }, { passive: false });

        openBtn.addEventListener('click',  () => openPanel());
        closeBtn.addEventListener('click', () => closePanel(false));
    });
};

// ---- Workflow + Services ----

const initWorkflowAndServices = (container) => {
    const section   = container.querySelector('#wf-hz-section');
    const wfLayer   = container.querySelector('#wf-layer');
    const hzLayer   = container.querySelector('#hz-layer');
    const hzTrack   = container.querySelector('#hz-track');
    const tlFill    = container.querySelector('#tl-fill');
    const tlHint    = container.querySelector('#tl-hint');
    const tlCounter = container.querySelector('#tl-counter');
    const hzFill    = container.querySelector('#hz-fill');
    const hzCounter = container.querySelector('#hz-counter');
    const steps     = gsap.utils.toArray('#wf-hz-section .tl-step');
    const hzSlides  = gsap.utils.toArray('#hz-track .hz-slide');

    // ── Slide intro « Je vous propose » ──
    const hzIntroTitle   = container.querySelector('#hz-intro-title');
    const hzIntroP1      = container.querySelector('#hz-intro-p1');
    const hzIntroP2      = container.querySelector('#hz-intro-p2');
    const hzIntroGraphic = container.querySelector('#hz-intro-graphic');
    const hzIntroLine    = container.querySelector('#hz-intro-line');
    const hzIntroNum     = container.querySelector('#hz-intro-num');
    const hzIntroLabel   = container.querySelector('#hz-intro-label');
    const hzSlideNum01   = container.querySelector('#hz-slide-num-01');
    const hzServiceNum02 = container.querySelector('#hz-service-num-02');

    if (!section || !wfLayer || !hzLayer || !hzTrack) return;

    const stepsCount  = steps.length;
    const slidesCount = hzSlides.length;

    const stepRevealed = new Array(stepsCount).fill(false);
    let   scrollLocked = false;

    // ── Couches : services visibles d'abord, workflow en attente à droite ──
    // GSAP reste propriétaire du transform des deux couches (pas de classe translate Tailwind)
    gsap.set(hzLayer, { x: 0, xPercent: 0 });
    gsap.set(wfLayer, { x: 0, xPercent: 100 });

    // État initial des paragraphes et du graphic
    if (hzIntroP1)      gsap.set(hzIntroP1,      { opacity: 0, filter: 'blur(10px)', y: 20 });
    if (hzIntroP2)      gsap.set(hzIntroP2,      { opacity: 0, filter: 'blur(10px)', y: 20 });
    if (hzIntroGraphic) gsap.set(hzIntroGraphic,  { opacity: 0 });
    if (hzSlideNum01)   gsap.set(hzSlideNum01,   { opacity: 0 });
    if (hzServiceNum02) gsap.set(hzServiceNum02,  { opacity: 0 });

    const dotPercent = (step) => {
        const rail     = container.querySelector('#tl-rail');
        const railRect = rail.getBoundingClientRect();
        const dot      = step.querySelector('.tl-dot');
        const dotRect  = dot.getBoundingClientRect();
        return (dotRect.left + dotRect.width / 2 - railRect.left) / railRect.width * 100;
    };

    // ── H2 « Je vous propose » : révélé à l'arrivée de la section ──
    if (hzIntroTitle) {
        gsap.set(hzIntroTitle, { opacity: 0, filter: 'blur(14px)', y: -50 });
        ScrollTrigger.create({
            trigger: section,
            start: 'top 85%',
            onEnter: () => {
                gsap.to(hzIntroTitle, {
                    opacity: 1, filter: 'blur(0px)', y: 0,
                    duration: 1.2, ease: 'power3.out', overwrite: 'auto',
                });
            },
            onLeaveBack: () => {
                gsap.to(hzIntroTitle, {
                    opacity: 0, filter: 'blur(14px)', y: -50,
                    duration: 0.6, ease: 'power2.in', overwrite: 'auto',
                });
            },
        });
    }

    // ── H2 « Workflow » : caché jusqu'à la bascule ──
    const wfTitle = container.querySelector('#wf-title');
    if (wfTitle) gsap.set(wfTitle, { scale: 8, opacity: 0, transformOrigin: 'center center' });

    // ── État initial slides services (hors slide intro index 0) ──
    hzSlides.forEach((slide, i) => {
        if (i === 0) return; // slide intro — géré séparément
        const title = slide.querySelector('.hz-title');
        const index = slide.querySelector('.hz-index');
        const body  = slide.querySelector('.hz-body');
        const cta   = slide.querySelector('.hz-cta');

        if (title) gsap.set(title, { opacity: 0, filter: 'blur(12px)', y: -60, transformOrigin: 'left top' });
        if (index) gsap.set(index, { opacity: 0, y: 24 });
        if (body)  gsap.set(body,  { opacity: 0, filter: 'blur(8px)', y: 24 });
        if (cta)   gsap.set(cta,   { opacity: 0, y: 20 });
    });

    // ── Cache des éléments slides — évite querySelector à chaque frame ──
    const slideCache = hzSlides.slice(1).map(slide => ({
        slide,
        title:   slide.querySelector('.hz-title'),
        index:   slide.querySelector('.hz-index'),
        body:    slide.querySelector('.hz-body'),
        cta:     slide.querySelector('.hz-cta'),
        bgWrap:  slide.querySelector('.hz-bg-wrap'),
        visible: false, // état pour éviter gsap.to à chaque frame
    }));

    // ── Cache maxTranslate — évite reflow scrollWidth à chaque frame ──
    let maxTranslate = 0;
    const refreshMaxTranslate = () => {
        maxTranslate = hzTrack.scrollWidth - window.innerWidth;
    };
    refreshMaxTranslate();
    ScrollTrigger.addEventListener('refreshInit', refreshMaxTranslate);

    // ── Workflow : révélation des étapes ──
    const updateWorkflow = (wfP) => {
        if (tlHint) gsap.set(tlHint, { opacity: wfP > 0.05 ? 0 : 1 });
        const shown = wfP > 0 ? Math.min(Math.ceil(wfP * stepsCount), stepsCount) : 0;
        if (tlCounter) tlCounter.textContent = shown > 0 ? `${shown} / ${stepsCount}` : `— / ${stepsCount}`;

        steps.forEach((step, i) => {
            const threshold = (i + 1) / (stepsCount + 1);
            const dot   = step.querySelector('.tl-dot');
            const num   = step.querySelector('.tl-num');
            const title = step.querySelector('.tl-title');
            const body  = step.querySelector('.tl-body');

            if (wfP >= threshold + 0.02 && !stepRevealed[i]) {
                stepRevealed[i] = true;
                gsap.to(dot, { backgroundColor: '#f5f5f5', borderColor: '#f5f5f5', scale: 1.5, duration: 0.5, ease: 'power3.out', overwrite: 'auto' });
                // Dernière étape : la ligne va jusqu'au bout du rail (plus de bascule après pour la compléter)
                const isLast = i === stepsCount - 1;
                gsap.to(tlFill, { width: isLast ? '100%' : dotPercent(step) + '%', duration: isLast ? 0.9 : 0.6, ease: 'power3.out', overwrite: 'auto' });
                gsap.to([num, title, body], { opacity: 1, y: 0, duration: 0.5, stagger: 0.06, ease: 'power2.out', overwrite: 'auto' });
            }
            if (wfP < threshold && stepRevealed[i]) {
                stepRevealed[i] = false;
                gsap.to(dot, { backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.3)', scale: 1, duration: 0.3, ease: 'power2.in', overwrite: 'auto' });
                gsap.to([num, title, body], { opacity: 0, y: 0, duration: 0.25, ease: 'power2.in', overwrite: 'auto' });
                const prevStep = steps[i - 1];
                gsap.to(tlFill, { width: prevStep ? dotPercent(prevStep) + '%' : '0%', duration: 0.4, ease: 'power3.out', overwrite: 'auto' });
            }
        });
    };

    // ── Slide intro : paragraphes + graphic ──
    const updateIntro = (introP) => {
        const p1Progress = clamp01(introP / 0.45);
        const p2Progress = clamp01((introP - 0.35) / 0.50);

        if (hzIntroP1) gsap.set(hzIntroP1, { opacity: p1Progress, filter: `blur(${(1 - p1Progress) * 10}px)`, y: (1 - p1Progress) * 20 });
        if (hzIntroP2) gsap.set(hzIntroP2, { opacity: p2Progress, filter: `blur(${(1 - p2Progress) * 10}px)`, y: (1 - p2Progress) * 20 });
        if (hzIntroGraphic) hzIntroGraphic.style.opacity = Math.min(introP * 3, 1);
        if (hzIntroLine) {
            hzIntroLine.style.width   = (introP * 100) + '%';
            hzIntroLine.style.opacity = Math.min(0.15 + introP * 1.5, 1);
        }
        if (hzIntroNum)   hzIntroNum.style.opacity   = Math.min(introP * 2, 1);
        if (hzIntroLabel) hzIntroLabel.style.opacity = Math.min(introP * 2, 1);
    };

    // ── Services : défilement horizontal ──
    const updateServices = (hzP) => {
        if (hzFill) hzFill.style.width = (hzP * 100) + '%';
        if (hzCounter) {
            const active = Math.min(Math.floor(hzP * (slidesCount - 1) + 0.15), slidesCount - 1);
            hzCounter.textContent = String(active + 1).padStart(2, '0') + ' / ' + String(slidesCount).padStart(2, '0');
        }

        gsap.set(hzTrack, { x: -hzP * maxTranslate });

        slideCache.forEach((cached, ci) => {
            const { title, index, body, cta, bgWrap } = cached;
            const i     = ci + 1;
            const ratio = i / (slidesCount - 1);
            const dist  = hzP - ratio;
            const absDist = Math.abs(dist);

            // ── Clip image — uniquement sur les slides non-derniers ──
            const isLastSlide = ci === slideCache.length - 1;
            const CLIP_OUT = 0.45;
            if (bgWrap && !isLastSlide) {
                if (dist >= 0 && dist < CLIP_OUT) {
                    const right = Math.round((dist / CLIP_OUT) * 100);
                    gsap.set(bgWrap, { clipPath: `inset(0 ${right}% 0 0)` });
                } else if (dist < 0) {
                    gsap.set(bgWrap, { clipPath: 'inset(0 0% 0 0)' });
                } else {
                    gsap.set(bgWrap, { clipPath: 'inset(0 100% 0 0)' });
                }
            }

            // ── Reveal slides — gsap.to uniquement au franchissement du seuil ──
            const shouldBeVisible = absDist < 0.35;
            if (shouldBeVisible && !cached.visible) {
                cached.visible = true;
                if (ci === 0 && hzSlideNum01)   gsap.to(hzSlideNum01,  { opacity: 1, duration: 0.8, ease: 'power2.out', overwrite: true });
                if (ci === 1 && hzServiceNum02) gsap.to(hzServiceNum02, { opacity: 1, duration: 0.8, ease: 'power2.out', overwrite: true });
                if (title) gsap.to(title, { opacity: 1, filter: 'blur(0px)', y: 0, duration: 1.0, ease: 'power3.out', overwrite: true });
                if (index) gsap.to(index, { opacity: 1, y: 0, duration: 0.7, delay: 0.08, ease: 'power2.out', overwrite: true });
                if (body)  gsap.to(body,  { opacity: 1, filter: 'blur(0px)', y: 0, duration: 0.9, delay: 0.18, ease: 'power2.out', overwrite: true });
                if (cta)   gsap.to(cta,   { opacity: 1, y: 0, duration: 0.7, delay: 0.3, ease: 'power2.out', overwrite: true });
            } else if (!shouldBeVisible && cached.visible) {
                cached.visible = false;
                if (ci === 0 && hzSlideNum01)   gsap.to(hzSlideNum01,  { opacity: 0, duration: 0.4, ease: 'power2.in', overwrite: true });
                if (ci === 1 && hzServiceNum02) gsap.to(hzServiceNum02, { opacity: 0, duration: 0.4, ease: 'power2.in', overwrite: true });
                if (title) gsap.to(title, { opacity: 0, filter: 'blur(12px)', y: -60, duration: 0.35, ease: 'power2.in', overwrite: true });
                if (index) gsap.to(index, { opacity: 0, y: 12, duration: 0.3, ease: 'power2.in', overwrite: true });
                if (body)  gsap.to(body,  { opacity: 0, filter: 'blur(6px)', y: 12, duration: 0.3, ease: 'power2.in', overwrite: true });
                if (cta)   gsap.to(cta,   { opacity: 0, y: 10, duration: 0.25, ease: 'power2.in', overwrite: true });
            }
        });
    };

    ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
            if (scrollLocked) return;
            const p = self.progress;

            // Valeurs bornées : un scroll rapide qui saute une zone laisse
            // quand même chaque phase dans son état de début ou de fin.
            if (p <= HZ_END)  updateIntro(clamp01(p / INTRO_END));
            if (p <= WF_START) updateServices(clamp01((p - INTRO_END) / (HZ_END - INTRO_END)));
            updateWorkflow(p < WF_START ? 0 : clamp01((p - WF_START) / (WF_END - WF_START)));
        },
    });

    // ── Bascule services → workflow ──
    ScrollTrigger.create({
        trigger: section,
        // Positions calculées sur la hauteur scrollable (pas la hauteur totale)
        // pour coïncider avec le progress du ScrollTrigger principal
        start: () => `top+=${(WF_START - 0.02) * (section.offsetHeight - window.innerHeight)} top`,
        end:   () => `top+=${WF_START * (section.offsetHeight - window.innerHeight)} top`,
        invalidateOnRefresh: true,
        onEnter: () => {
            gsap.to(hzLayer, { xPercent: -100, duration: 0.6, ease: 'power2.inOut', overwrite: 'auto' });
            gsap.to(wfLayer, { xPercent: 0,    duration: 0.6, ease: 'power2.inOut', overwrite: 'auto' });

            if (wfTitle) {
                gsap.fromTo(wfTitle,
                    { scale: 8, opacity: 0, transformOrigin: 'center center' },
                    { scale: 1, opacity: 1, duration: 0.4, ease: 'power4.out', delay: 0.15, overwrite: 'auto' }
                );
            }
        },
        onLeaveBack: () => {
            gsap.to(hzLayer, { xPercent: 0,   duration: 0.6, ease: 'power2.inOut', overwrite: 'auto' });
            gsap.to(wfLayer, { xPercent: 100, duration: 0.6, ease: 'power2.inOut', overwrite: 'auto' });
            if (wfTitle) gsap.to(wfTitle, { opacity: 0, duration: 0.3, ease: 'power2.in', overwrite: 'auto',
                onComplete: () => gsap.set(wfTitle, { scale: 8 }) });
        },
    });

    initServicePanels(
        container,
        section,
        slidesCount,
        () => {
            scrollLocked = true;
            document.body.style.overflow = 'hidden';
            ScrollTrigger.getAll().forEach(t => t.disable());
        },
        (goNext, currentSlideIndex) => {
            scrollLocked = false;
            document.body.style.overflow = '';
            ScrollTrigger.getAll().forEach(t => t.enable());

            if (goNext) {
                const nextIndex = Math.min(currentSlideIndex + 1, slidesCount - 1);
                const target    = getSnapScrollY(section, nextIndex, slidesCount);
                window.scrollTo({ top: target, behavior: 'smooth' });
            }
        }
    );

    initProjectLinks(container);
};

// ---- Section « Qui » : titre, portrait, texte, fiche ----

const initAboutBio = (container) => {
    const section = container.querySelector('#about-bio');
    if (!section) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const title    = section.querySelector('#bio-title');
    const portrait = section.querySelector('#bio-portrait');
    const media    = portrait?.querySelector('.bio-media');
    const texts    = section.querySelectorAll('.bio-reveal');
    const facts    = section.querySelectorAll('.bio-fact');

    // Titre : même apparition que les grands titres de la page (flou → net)
    if (title) {
        gsap.fromTo(title, { opacity: 0, filter: 'blur(14px)', y: -40 }, {
            opacity: 1, filter: 'blur(0px)', y: 0, duration: 1.2, ease: 'power3.out',
            scrollTrigger: { trigger: title, start: 'top 85%', toggleActions: 'play none none reverse' },
        });
    }

    // Portrait : se découvre de bas en haut, léger dézoom de l'image
    if (portrait) {
        gsap.fromTo(portrait, { clipPath: 'inset(100% 0 0 0)' }, {
            clipPath: 'inset(0% 0 0 0)', duration: 1.4, ease: 'expo.inOut',
            scrollTrigger: { trigger: portrait, start: 'top 80%', toggleActions: 'play none none reverse' },
        });
        if (media) {
            gsap.fromTo(media, { scale: 1.15 }, {
                scale: 1, ease: 'none',
                scrollTrigger: { trigger: portrait, start: 'top bottom', end: 'bottom top', scrub: 1 },
            });
        }
    }

    // Survol : le second portrait arrive en colonnes décalées, depuis le côté d'entrée de la souris,
    // et repart vers le haut à la sortie (le mouvement continue au lieu de se rembobiner)
    const cols = portrait ? [...portrait.querySelectorAll('.bio-col')] : [];
    if (cols.length) {
        gsap.set(cols, { yPercent: 101 });
        let shown = false;
        const show = (fromRight) => {
            shown = true;
            gsap.fromTo(cols, { yPercent: 101 }, {
                yPercent: 0, duration: 0.75, ease: 'power3.inOut', overwrite: true,
                stagger: { each: 0.045, from: fromRight ? 'end' : 'start' },
            });
        };
        const hide = (fromRight) => {
            shown = false;
            gsap.to(cols, {
                yPercent: -101, duration: 0.75, ease: 'power3.inOut', overwrite: true,
                stagger: { each: 0.045, from: fromRight ? 'end' : 'start' },
            });
        };
        const side = (e) => {
            const r = portrait.getBoundingClientRect();
            return e.clientX > r.left + r.width / 2;
        };
        if (window.matchMedia('(hover: hover)').matches) {
            portrait.addEventListener('mouseenter', (e) => show(side(e)));
            portrait.addEventListener('mouseleave', (e) => hide(side(e)));
        } else {
            // Écrans tactiles : un toucher alterne les deux portraits
            portrait.addEventListener('click', () => (shown ? hide(false) : show(false)));
        }
    }

    // Texte : mots qui se précisent au scroll (même effet que l'intro de la page)
    texts.forEach((text) => {
        const words = splitWords(text);
        gsap.fromTo(words, { opacity: 0.15 }, {
            opacity: 1, ease: 'none', stagger: 0.05,
            scrollTrigger: { trigger: text, start: 'top 80%', end: 'bottom 55%', scrub: 1 },
        });
    });

    // Fiche : lignes l'une après l'autre
    if (facts.length) {
        gsap.fromTo(facts, { opacity: 0, y: 16 }, {
            opacity: 1, y: 0, duration: 0.8, stagger: 0.08, ease: 'power3.out',
            scrollTrigger: { trigger: facts[0], start: 'top 88%', toggleActions: 'play none none reverse' },
        });
    }
};

// ---- Export ----

export const initAboutPage = (container) => {
    const h1   = container.querySelector('#h1-wrapper');
    const p    = container.querySelector('#p-target');
    const zone = container.querySelector('#about-scroll-zone');
    const para = container.querySelector('.word-reveal');

    if (!h1 || !p || !zone || !para) return;

    gsap.to(h1, {
        y: () => p.getBoundingClientRect().top - h1.getBoundingClientRect().bottom,
        ease: 'none',
        scrollTrigger: {
            id: 'about-h1-scroll',
            trigger: zone,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 0.5,
            invalidateOnRefresh: true,
        },
    });

    const words = splitWords(para);
    if (!words.length) return;

    const teaserCount = 3;
    const teaserWords = words.slice(0, teaserCount);
    const restWords   = words.slice(teaserCount);
    const midpoint    = Math.floor(restWords.length * 0.45);
    const wordsB      = restWords.slice(0, midpoint);
    const wordsC      = restWords.slice(midpoint);

    gsap.set(teaserWords, { opacity: 0.6, filter: 'blur(0px)', y: 0 });
    gsap.set(wordsB,      { opacity: 0,   filter: 'blur(10px)', y: 8  });
    gsap.set(wordsC,      { opacity: 0,   filter: 'blur(14px)', y: 12 });

    const scrollConfig = {
        scrollTrigger: {
            trigger: zone,
            start: '-9% top',
            end: 'bottom 60%',
            scrub: 2,
        },
    };

    gsap.fromTo(teaserWords, { opacity: 0.6, filter: 'blur(0px)',  y: 0  }, { opacity: 1, filter: 'blur(0px)', y: 0, ease: 'power2.out', stagger: 0.03, ...scrollConfig });
    gsap.fromTo(wordsB,      { opacity: 0,   filter: 'blur(10px)', y: 8  }, { opacity: 1, filter: 'blur(0px)', y: 0, ease: 'power2.out', stagger: 0.05, ...scrollConfig });
    gsap.fromTo(wordsC,      { opacity: 0,   filter: 'blur(14px)', y: 12 }, { opacity: 1, filter: 'blur(0px)', y: 0, ease: 'power2.out', stagger: 0.06, ...scrollConfig });








    initAboutBio(container);
    initWorkflowAndServices(container);
};